import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Bell, 
  Plus, 
  Trash2, 
  TrendingUp, 
  TrendingDown,
  AlertCircle,
  CheckCircle,
  Lock
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import AppLayout from '../components/AppLayout';

const AlertsPage = () => {
  const { api, user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tierInfo, setTierInfo] = useState(null);
  
  // Create alert dialog
  const [createDialog, setCreateDialog] = useState(false);
  const [symbol, setSymbol] = useState('');
  const [targetPrice, setTargetPrice] = useState('');
  const [condition, setCondition] = useState('above');
  const [creating, setCreating] = useState(false);
  const [currentPrice, setCurrentPrice] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [alertsRes, tierRes] = await Promise.all([
        api.get('/alerts'),
        api.get('/settings/tier')
      ]);
      setAlerts(alertsRes.data);
      setTierInfo(tierRes.data);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchCurrentPrice = async (sym) => {
    if (!sym || sym.length < 1) {
      setCurrentPrice(null);
      return;
    }
    try {
      const response = await api.get(`/stocks/${sym}/quote`);
      setCurrentPrice(response.data.current_price);
    } catch (error) {
      setCurrentPrice(null);
    }
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchCurrentPrice(symbol);
    }, 500);
    return () => clearTimeout(debounce);
  }, [symbol]);

  const handleCreateAlert = async () => {
    if (!symbol || !targetPrice) {
      toast.error('Please fill in all fields');
      return;
    }

    setCreating(true);
    try {
      await api.post('/alerts', {
        symbol: symbol.toUpperCase(),
        target_price: parseFloat(targetPrice),
        condition
      });
      toast.success(`Alert created for ${symbol.toUpperCase()}`);
      setCreateDialog(false);
      setSymbol('');
      setTargetPrice('');
      setCondition('above');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create alert');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteAlert = async (alertId) => {
    try {
      await api.delete(`/alerts/${alertId}`);
      toast.success('Alert deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete alert');
    }
  };

  const handleCheckAlerts = async () => {
    try {
      const response = await api.post('/alerts/check');
      if (response.data.triggered_alerts.length > 0) {
        response.data.triggered_alerts.forEach(alert => {
          toast.success(`🔔 ${alert.symbol} hit $${alert.triggered_price}!`);
        });
      } else {
        toast.info(`Checked ${response.data.checked} alerts - none triggered`);
      }
      fetchData();
    } catch (error) {
      toast.error('Failed to check alerts');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isPro = tierInfo?.tier === 'paid';

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-pulse-subtle text-primary font-serif text-xl">Loading alerts...</div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-8" data-testid="alerts-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-serif text-3xl font-semibold text-[#1F2937] flex items-center gap-3">
              <Bell className="w-8 h-8 text-[#1A4D2E]" />
              Price Alerts
            </h1>
            <p className="text-gray-500 mt-1">Get notified when stocks hit your target prices</p>
          </div>
          <div className="flex gap-3">
            {isPro && alerts.length > 0 && (
              <Button
                variant="outline"
                onClick={handleCheckAlerts}
                className="border-[#1A4D2E]/20 text-[#1A4D2E]"
              >
                Check Now
              </Button>
            )}
            <Button
              onClick={() => isPro ? setCreateDialog(true) : toast.error('Price alerts are a Pro feature')}
              className={isPro ? "bg-[#C1F23D] text-[#1A4D2E] hover:bg-[#b2e030]" : "bg-gray-300 cursor-not-allowed"}
              data-testid="create-alert-btn"
            >
              {isPro ? <Plus className="w-4 h-4 mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
              New Alert
            </Button>
          </div>
        </div>

        {/* Pro Feature Notice */}
        {!isPro && (
          <Card className="bg-gradient-to-r from-[#1A4D2E] to-[#2D5A3D] text-white border-0">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                    <Bell className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Price Alerts are a Pro Feature</h3>
                    <p className="text-white/80 text-sm">Upgrade to Pro to set up to 50 price alerts</p>
                  </div>
                </div>
                <Button 
                  className="bg-[#C1F23D] text-[#1A4D2E] hover:bg-[#b2e030]"
                  onClick={() => window.location.href = '/settings'}
                >
                  Upgrade to Pro
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Alerts List */}
        <Card className="bg-white border-stone-100 shadow-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-serif text-xl text-[#1F2937]">Your Alerts</CardTitle>
              {isPro && (
                <Badge variant="outline" className="text-[#1A4D2E] border-[#1A4D2E]/20">
                  {alerts.filter(a => !a.triggered).length} / {tierInfo?.limits?.alerts || 50} active
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {alerts.length === 0 ? (
              <div className="text-center py-12">
                <Bell className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 mb-2">No alerts set up yet</p>
                {isPro ? (
                  <p className="text-sm text-gray-400">Create an alert to get notified when prices move</p>
                ) : (
                  <p className="text-sm text-gray-400">Upgrade to Pro to use price alerts</p>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {alerts.map((alert) => (
                  <div
                    key={alert.id}
                    className={`flex items-center justify-between p-4 rounded-lg border ${
                      alert.triggered 
                        ? 'bg-green-50 border-green-200' 
                        : 'bg-white border-stone-100'
                    }`}
                    data-testid={`alert-${alert.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                        alert.triggered ? 'bg-green-100' : 'bg-stone-100'
                      }`}>
                        {alert.triggered ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : alert.condition === 'above' ? (
                          <TrendingUp className="w-5 h-5 text-green-600" />
                        ) : (
                          <TrendingDown className="w-5 h-5 text-red-500" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-[#1F2937]">{alert.symbol}</p>
                          <Badge variant={alert.triggered ? "default" : "secondary"} className={alert.triggered ? "bg-green-600" : ""}>
                            {alert.triggered ? 'Triggered' : 'Active'}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-500">
                          Alert when price goes {alert.condition} {formatCurrency(alert.target_price)}
                        </p>
                        <p className="text-xs text-gray-400">
                          Created {formatDate(alert.created_at)}
                          {alert.triggered && ` • Triggered at ${formatCurrency(alert.triggered_price)}`}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteAlert(alert.id)}
                      className="text-gray-400 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create Alert Dialog */}
        <Dialog open={createDialog} onOpenChange={setCreateDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl">Create Price Alert</DialogTitle>
              <DialogDescription>
                Get notified when a stock reaches your target price
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="symbol">Stock Symbol</Label>
                <Input
                  id="symbol"
                  placeholder="e.g., AAPL"
                  value={symbol}
                  onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                  className="uppercase"
                  data-testid="alert-symbol-input"
                />
                {currentPrice && (
                  <p className="text-sm text-gray-500">
                    Current price: {formatCurrency(currentPrice)}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Condition</Label>
                <Select value={condition} onValueChange={setCondition}>
                  <SelectTrigger data-testid="alert-condition-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="above">
                      <span className="flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-green-600" />
                        Price goes above
                      </span>
                    </SelectItem>
                    <SelectItem value="below">
                      <span className="flex items-center gap-2">
                        <TrendingDown className="w-4 h-4 text-red-500" />
                        Price goes below
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="target">Target Price ($)</Label>
                <Input
                  id="target"
                  type="number"
                  step="0.01"
                  placeholder="e.g., 150.00"
                  value={targetPrice}
                  onChange={(e) => setTargetPrice(e.target.value)}
                  data-testid="alert-price-input"
                />
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => setCreateDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreateAlert}
                disabled={creating || !symbol || !targetPrice}
                className="bg-[#1A4D2E] hover:bg-[#143d24]"
                data-testid="confirm-alert-btn"
              >
                {creating ? 'Creating...' : 'Create Alert'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
};

export default AlertsPage;
